import { FC } from "react";
import { Card, CardContent } from "@/lib/shadcn/ui/card";
import { Cell, Label, Pie, PieChart, ResponsiveContainer } from "recharts";
import { CalendarIcon } from "@radix-ui/react-icons";
import ComparisonTable from "@/lib/shadcn/components/comparison-table";

interface OccupancyChartStatsCardProps {
    metric: string;
    totalOccupancy: number;
    groupOccupancies: { name: string; value: number }[];
    groupOccupanciesPreviousPeriod: { name: string; value: number }[];
    groupOccupanciesPreviousYear: { name: string; value: number }[];
    period: string;
    showPreviousYear: boolean;
    totalPreviousPeriod: number;
    totalPreviousYear: number;
}

const OccupancyChartStatsCard: FC<OccupancyChartStatsCardProps> = ({
    metric,
    totalOccupancy,
    groupOccupancies,
    groupOccupanciesPreviousPeriod,
    groupOccupanciesPreviousYear,
    period,
    totalPreviousPeriod,
    totalPreviousYear,
    showPreviousYear,
}) => {
    return (
        <Card className="h-full p-0">
            <CardContent className="pt-4 p-2">
                <div className="flex flex-col space-y-3">
                    {/* Header */}
                    <div>
                        <h2 className="text-muted-foreground mb-0.5">Total {metric}</h2>
                    </div>

                    {/* Gauge Chart */}
                    <div className="h-[100px] w-full flex items-center justify-center">
                        <div className="relative h-24 w-48">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={[{ value: Math.round(totalOccupancy) }, { value: 100 - Math.fround(totalOccupancy) }]}
                                        cx="50%"
                                        cy="100%"
                                        startAngle={180}
                                        endAngle={0}
                                        innerRadius={70}
                                        outerRadius={90}
                                        paddingAngle={0}
                                        dataKey="value"
                                    >
                                        <Cell fill="#f0bc74" />
                                        <Cell fill="#e5e7eb" />
                                        <Label
                                            value={`${Math.round(totalOccupancy)}%`}
                                            position="center"
                                            dy={-10}
                                            fontSize={20}
                                            fill="#000000"
                                        />
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Group Metrics */}
                    <div className="space-y-1">
                        {groupOccupancies.map((metric, index) => (
                            <div key={index} className="space-y-1">
                                <div className="flex justify-between items-center">
                                    <span className="text-xs font-medium">{metric.name}</span>
                                    <span className="text-xs">{metric.value.toFixed(1)}%</span>
                                </div>
                                <div className="relative w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                        className="absolute left-0 top-0 h-full bg-slate-900 rounded-full transition-all duration-300"
                                        style={{ width: `${metric.value}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Period and Table Section */}
                    <div className="space-y-2">
                        {/* Period */}
                        <div className="flex items-center text-xs text-muted-foreground">
                            <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
                            <span>{period}</span>
                        </div>

                        {/* Comparison Table */}
                        <ComparisonTable
                            columnNames={groupOccupancies.map((item) => item.name)}
                            period={period}
                            showPreviousYear={showPreviousYear}
                            periodData={groupOccupanciesPreviousPeriod}
                            yearData={groupOccupanciesPreviousYear}
                            currentData={groupOccupancies}
                            totalCurrent={totalOccupancy}
                            totalPreviousPeriod={totalPreviousPeriod}
                            totalPreviousYear={totalPreviousYear}
                            valueFormatter={(value) => `${value.toFixed(1)}%`}
                        />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default OccupancyChartStatsCard;
